import { Card } from './types';

/**
 * Pure helpers for `story_mode` cards — a graph of scenes keyed by id, where each choice
 * names the `next` scene. Mirrors how alpha-web-fe's TestStory walks it: an `isEnd` scene
 * ignores its choices, and an ending's outcome is `endType ?? (isGoodPath ? 'good' : —)`.
 */

export type EndType = 'good' | 'mid' | 'bad';

export interface StoryChoice {
  text: string;
  next: string;
  [key: string]: unknown;
}

export interface StoryScene {
  text: string;
  choices?: StoryChoice[];
  isEnd?: boolean;
  endType?: EndType;
  lesson?: string;
  isGoodPath?: boolean;
  [key: string]: unknown;
}

export interface StoryCard extends Card {
  startScene: string;
  scenes: Record<string, StoryScene>;
}

export function endTypeOf(scene: StoryScene): EndType | undefined {
  return scene.endType ?? (scene.isGoodPath ? 'good' : undefined);
}

/**
 * One row of the story map. `scene` nodes are expanded; a `repeat` points at a scene that is
 * already expanded higher up (or is a loop back), and `missing` at a key with no scene.
 */
export interface StoryNode {
  key: string;
  /** Text of the choice that leads here; null for the start scene. */
  via: string | null;
  /** Scene keys from the start to this node, inclusive. */
  path: string[];
  kind: 'scene' | 'repeat' | 'missing';
  children: StoryNode[];
}

/**
 * Builds the story map breadth-first, so every scene is expanded once at its shallowest
 * position — loops and merging paths show up as `repeat` leaves instead of recursing.
 */
export function buildStoryTree(card: StoryCard): StoryNode | null {
  const { scenes, startScene } = card;
  if (!scenes[startScene]) return null;

  const root: StoryNode = { key: startScene, via: null, path: [startScene], kind: 'scene', children: [] };
  const seen = new Set<string>([startScene]);
  const queue: StoryNode[] = [root];

  while (queue.length > 0) {
    const node = queue.shift()!;
    const scene = scenes[node.key];
    if (scene.isEnd) continue;
    for (const choice of scene.choices ?? []) {
      const next = choice.next;
      const child: StoryNode = { key: next, via: choice.text, path: [...node.path, next], kind: 'scene', children: [] };
      if (!scenes[next]) {
        child.kind = 'missing';
      } else if (seen.has(next)) {
        child.kind = 'repeat';
      } else {
        seen.add(next);
        queue.push(child);
      }
      node.children.push(child);
    }
  }
  return root;
}

export function reachableScenes(card: StoryCard): Set<string> {
  const reachable = new Set<string>();
  const visit = (node: StoryNode) => {
    if (node.kind !== 'scene') return;
    reachable.add(node.key);
    node.children.forEach(visit);
  };
  const tree = buildStoryTree(card);
  if (tree) visit(tree);
  return reachable;
}

export function validateSceneKey(card: StoryCard, from: string, to: string): string | null {
  const key = to.trim();
  if (!key) return 'Scene id can\'t be empty';
  if (key !== from && card.scenes[key]) return `A scene "${key}" already exists`;
  return null;
}

/** Renames a scene in place (keeping key order) and repoints every link and the start. */
export function renameScene(card: StoryCard, from: string, to: string): StoryCard {
  const scenes: Record<string, StoryScene> = {};
  Object.keys(card.scenes).forEach((key) => {
    const scene = card.scenes[key];
    scenes[key === from ? to : key] = scene.choices
      ? { ...scene, choices: scene.choices.map((c) => (c.next === from ? { ...c, next: to } : c)) }
      : scene;
  });
  return { ...card, scenes, startScene: card.startScene === from ? to : card.startScene };
}

/** Removes a scene. Choices that led to it are kept, so the story map flags them for relinking. */
export function deleteScene(card: StoryCard, key: string): StoryCard {
  const scenes = { ...card.scenes };
  delete scenes[key];
  return { ...card, scenes };
}

export function newSceneKey(scenes: Record<string, StoryScene>): string {
  let n = Object.keys(scenes).length + 1;
  while (scenes[`scene_${n}`]) n++;
  return `scene_${n}`;
}

export function incomingLinks(card: StoryCard, key: string) {
  const links: { from: string; choiceIndex: number; text: string }[] = [];
  Object.keys(card.scenes).forEach((from) => {
    (card.scenes[from].choices ?? []).forEach((choice, choiceIndex) => {
      if (choice.next === key) links.push({ from, choiceIndex, text: choice.text });
    });
  });
  return links;
}

export function storyIssues(card: StoryCard): string[] {
  const scenes = card.scenes ?? {};
  const keys = Object.keys(scenes);
  if (keys.length === 0) return ['The story has no scenes'];

  const issues: string[] = [];
  const hasStart = !!scenes[card.startScene];
  if (!hasStart) issues.push(`Start scene "${card.startScene}" doesn't exist`);
  const reachable = reachableScenes(card);

  keys.forEach((key) => {
    const scene = scenes[key];
    if (!scene.text?.trim()) issues.push(`Scene "${key}" has no text`);
    if (scene.isEnd) {
      if (!endTypeOf(scene)) issues.push(`Ending "${key}" has no outcome type (the app shows it as a failure)`);
    } else {
      const choices = scene.choices ?? [];
      choices.forEach((choice, i) => {
        if (!choice.next) issues.push(`Scene "${key}": choice ${i + 1} has no target scene`);
        else if (!scenes[choice.next]) issues.push(`Scene "${key}": choice ${i + 1} leads to missing scene "${choice.next}"`);
      });
      if (choices.length === 0) issues.push(`Scene "${key}" has no choices and isn't marked as an ending`);
    }
    if (hasStart && !reachable.has(key)) issues.push(`Scene "${key}" can't be reached from the start`);
  });

  if (hasStart && !Array.from(reachable).some((key) => scenes[key].isEnd)) {
    issues.push('No ending can be reached from the start');
  }
  return issues;
}
